import {
  useClient,
  useDictionary,
  useItems,
  useOrderItem,
} from "@adamlip/okeoke.client"
import { Button, ButtonGroup } from "@nextui-org/react"
import BasketItemModal from "views/BasketModal/Slides/BasketItems/BasketItemModal"
import { useState, useContext, useEffect } from "react"
import LoadSvg from "utils/LoadSvg-v3"
import { AppParamsContext } from "contexts/AppParamsContext"
import MenuItem from "./MenuItem"
import useBasket from "hooks/useBasket"
import FormattedNumber from "components/FormattedNumber"

export default function BasketItem({ orderItemUUID, orderItems, menuSize }) {
  const { assembledItem, setCount, update } = useOrderItem(
    null,
    null,
    orderItemUUID
  )
  const { sizes } = useItems()
  const { getValue } = useDictionary()
  const { getName } = useClient()
  const { appParams } = useContext(AppParamsContext)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedStyle, setSelectedStyle] = useState(false)
  const [showSizeName, setShowSizeName] = useState(true)
  const { order } = useBasket()

  const removeItem = () => {
    order.updateBasketItemCount(orderItemUUID, 0)
    for (let childOrder of orderItems.filter(
      (item) => item.parentOrderItemUUID === orderItemUUID
    )) {
      order.updateBasketItemCount(childOrder.orderItemUUID, 0)
    }
  }

  const increaseCount = () => {
    if (menuSize) return null
    setCount(assembledItem.count + 1)
    update()
  }

  const decreaseCount = () => {
    if (menuSize) return null
    setCount(assembledItem.count - 1)
    update()
  }

  useEffect(() => {
    switch (appParams?.basketModal?.items?.itemStyle) {
      case "2":
        setSelectedStyle("border-2 border-[var(--main-text-color)]")
        break
      default:
        setSelectedStyle("shadow-sm bg-[var(--main-secondary-bg-color)]")
    }

    if (appParams?.basketModal?.items?.hideSizeName) {
      let haveMultipleItemsFromThis =
        orderItems
          .filter((item) => item?.itemUUID === assembledItem?.uuid)
          ?.filter((item) => item?.count > 0)?.length > 1
      let isThisItemHaveMultipleSizes = assembledItem?.visibleSizes > 1
      setShowSizeName(
        haveMultipleItemsFromThis && isThisItemHaveMultipleSizes ? true : false
      )
    } else {
      setShowSizeName(true)
    }
  }, [appParams, orderItems, assembledItem])
  if (assembledItem == null) return null
  if (assembledItem?.count === 0) return null

  return (
    <div
      className={`basketItem-root ${selectedStyle} w-full mb-2 ${assembledItem.valid
        ? "valid"
        : assembledItem.count > 0
          ? "invalid"
          : "valid"
        } ${assembledItem.count > 0 ? "in-basket" : "not-in-basket"} ${assembledItem?.availability?.visible === 1 ? "visible" : "not-visible"
        }`}
      onClick={() => {
        setIsOpen(true)
      }}
    >
      <BasketItemModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        orderItemUUID={orderItemUUID}
      />
      <div className="basketItem-name-root grow">
        <div className="flex flex-col items-start mb-2">
          <div
            className={`${appParams?.styles?.fonts?.capitalize
              ? "font-extrabold uppercase"
              : ""
              } basketItem-name  text-md`}
          >
            {getName(assembledItem.name)}
            {showSizeName && (
              <>
                {" "}
                ( {getName(
                  sizes?.[assembledItem?.selectedSizeUUID]?.name
                )} ){" "}
              </>
            )}{" "}
            {assembledItem.takeAway === 1 ? "TA" : ""}
          </div>
          <div className="text-sm font-normal opacity-60">
            {assembledItem.price > 0 && (
              <FormattedNumber value={assembledItem.price} />
            )}
            {assembledItem.depositPrice > 0 &&
              ` (+${assembledItem.depositPrice} Ft ${getValue(
                "app_v2_item_deposit_price"
              )})`}
          </div>
        </div>
        {showSizeName && (
          <div className="basketItem-size font-smallest">
            {getName(
              assembledItem?.sizes[assembledItem.selectedSizeUUID]?.name
            )}
          </div>
        )}
        <div className="basketItem-modifiers font-smallest">
          {(assembledItem?.modifierGroups || [])
            ?.filter((group) =>
              group?.modifiers?.some((modifier) => modifier.count > 0)
            )
            .map((group) => (
              <div
                key={group.uuid}
                className={`basketItem-modifierGroup mb-2 text-xs ${group.valid
                  ? "valid"
                  : assembledItem.count > 0
                    ? "invalid"
                    : "valid"
                  }`}
              >
                <p className="font-semibold"> {getName(group?.name)}:</p>
                {group.modifiers
                  .filter((modifier) => modifier.count > 0)
                  .map((modifier) => (
                    <div
                      key={modifier.uuid}
                      className={`basketItem-modifier ${modifier.valid
                        ? "valid"
                        : assembledItem.count > 0
                          ? "invalid"
                          : "valid"
                        }`}
                    >
                      {`${modifier.count == null || modifier.count === 1
                        ? ""
                        : modifier.count
                        } ${getName(modifier.name)}`}
                    </div>
                  ))}
              </div>
            ))}
          <div className="basketItem-modifier">{assembledItem.notes}</div>
          <div className="">
            {orderItems
              .filter((item) => item.parentOrderItemUUID === orderItemUUID)
              .map((item) => (
                <MenuItem key={item.orderItemUUID} item={item} />
              ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end items-end">
        <ButtonGroup className="gap-1">
          {!menuSize && (
            <Button
              size="sm"
              isIconOnly
              color="secondary"
              className="!rounded-[var(--order-button-border-radius)] text-[var(--button2-text-color)]"
              onClick={() => decreaseCount()}
            >
              <LoadSvg className="basket-item-svg" name="minusIcon" />
            </Button>
          )}
          {!menuSize && (
            <Button
              size="sm"
              isIconOnly
              className="!rounded-[var(--order-button-border-radius)] bg-[var(--)] font-bold"
            >
              {assembledItem?.count}
            </Button>
          )}
          {!menuSize && (
            <Button
              size="sm"
              isIconOnly
              color="secondary"
              className="!rounded-[var(--order-button-border-radius)]"
              onClick={() => increaseCount()}
            >
              <LoadSvg className="basket-item-svg" name="plusIcon" />
            </Button>
          )}
          <Button onPress={() => removeItem()}>
            <LoadSvg className="basket-item-svg" name="trash" />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
